import {
  StorageCustody,
  StoragePlacement,
  StorageTransitSource,
  type StorageObjectGroupStateRecord,
  type StorageUnitRecordList
} from '@navio/nsdk';
import { withCustomerStatusLabel } from './storageEventCopy';
import { storageStatusCopy } from './storageStatusCopy';

export type StorageGroupStatusKind =
  | 'noHistory'
  | 'lost'
  | 'discarded'
  | 'withCustomer'
  | 'notReceived'
  | 'pickedUpFromCustomer'
  | 'pickedUpFromSupplier'
  | 'inTransitUnknownOrigin'
  | 'processing'
  | 'inTransit'
  | 'inStorage'
  | 'atFacility';

type StorageGroupStatus = {
  kind: StorageGroupStatusKind;
  label: string;
};

const inTransitStatusBySource: Record<StorageTransitSource, StorageGroupStatus> = {
  [StorageTransitSource.CUSTOMER]: { kind: 'pickedUpFromCustomer', label: storageStatusCopy.pickedUpFromCustomer },
  [StorageTransitSource.SUPPLIER]: { kind: 'pickedUpFromSupplier', label: storageStatusCopy.pickedUpFromSupplier },
  [StorageTransitSource.FACILITY]: { kind: 'inTransit', label: storageStatusCopy.inTransit },
  [StorageTransitSource.UNKNOWN]: { kind: 'inTransitUnknownOrigin', label: storageStatusCopy.inTransitUnknownOrigin }
};

export const getStorageGroupStatus = (state: StorageObjectGroupStateRecord | null | undefined): StorageGroupStatus => {
  if (!state) {
    return { kind: 'noHistory', label: storageStatusCopy.noHistory };
  }

  switch (state.custody.value) {
    case StorageCustody.LOST:
      return { kind: 'lost', label: storageStatusCopy.lost };
    case StorageCustody.DISCARDED:
      return { kind: 'discarded', label: storageStatusCopy.discarded };
    case StorageCustody.WITH_CUSTOMER:
      return state.customerHandoff
        ? { kind: 'withCustomer', label: withCustomerStatusLabel(state.customerHandoff.value) }
        : { kind: 'notReceived', label: storageStatusCopy.notReceived };
    case StorageCustody.AT_FACILITY:
      // Custody alone does not name a status here — the placement switch below does.
      break;
  }

  switch (state.placement.value) {
    case StoragePlacement.PROCESSING:
      return { kind: 'processing', label: storageStatusCopy.beingWorkedOn };
    case StoragePlacement.IN_TRANSIT:
      return getInTransitStatus(state.transitSource?.value);
    case StoragePlacement.AT_UNIT:
      return { kind: 'inStorage', label: storageStatusCopy.inStorage };
    default:
      return { kind: 'atFacility', label: storageStatusCopy.atFacility };
  }
};

// Rows written before the backend stored the source have none yet.
export const getInTransitStatus = (transitSource: StorageTransitSource | null | undefined): StorageGroupStatus =>
  transitSource ? inTransitStatusBySource[transitSource] : { kind: 'inTransit', label: storageStatusCopy.inTransit };

export const isStorageGroupPickedUpAwaitingFacility = (
  state: StorageObjectGroupStateRecord | null | undefined
): boolean => getStorageGroupStatus(state).kind === 'pickedUpFromCustomer';

/** Moved at a facility or from an unknown origin, and not yet on a storage unit. */
export const isStorageGroupWithoutLocation = (state: StorageObjectGroupStateRecord | null | undefined): boolean => {
  const kind = getStorageGroupStatus(state).kind;
  return kind === 'inTransit' || kind === 'inTransitUnknownOrigin';
};

export const isStorageGroupWithCustomer = (state: StorageObjectGroupStateRecord | null | undefined): boolean =>
  state?.custody.value === StorageCustody.WITH_CUSTOMER;

type StorageGroupWithOptionalState = {
  state?: StorageObjectGroupStateRecord | null;
  latestEvent?: {
    isDeliveredToCustomer?: boolean;
    isDeliveryToCustomer?: boolean;
  } | null;
};

export const isStorageGroupWithCustomerFromGroup = (group: StorageGroupWithOptionalState): boolean => {
  if (group.state) {
    return isStorageGroupWithCustomer(group.state);
  }
  return !!(group.latestEvent?.isDeliveredToCustomer || group.latestEvent?.isDeliveryToCustomer);
};

const formatStorageUnitLine = (unit: StorageUnitRecordList): string => {
  const areaName = unit.storageArea.name.trim();
  const unitName = unit.name.trim();
  if (areaName && areaName.localeCompare(unitName, undefined, { sensitivity: 'accent' }) !== 0) {
    return `${areaName} • ${unitName}`;
  }
  return unitName || areaName || '';
};

type StorageGroupLocationDisplay = {
  facilityName: string | null;
  unitLine: string | null;
  showOrigin: boolean;
  originFacilityName: string | null;
  originUnitLine: string | null;
};

const hasVisibleLocation = (display: StorageGroupLocationDisplay): boolean =>
  !!(display.facilityName || display.unitLine || display.originFacilityName || display.originUnitLine);

/** Whether the group sits on a unit of this facility. */
export const isStorageGroupAtFacility = (
  state: StorageObjectGroupStateRecord | null | undefined,
  storageFacilityId: number
): boolean =>
  state?.custody.value === StorageCustody.AT_FACILITY &&
  state.placement.value === StoragePlacement.AT_UNIT &&
  state.facility?.id === storageFacilityId;

export const getStorageGroupLocationDisplay = (
  state: StorageObjectGroupStateRecord | null | undefined
): StorageGroupLocationDisplay | null => {
  if (!state) {
    return null;
  }

  if (state.custody.value === StorageCustody.WITH_CUSTOMER) {
    return null;
  }

  if (state.placement.value === StoragePlacement.IN_TRANSIT) {
    const display: StorageGroupLocationDisplay = {
      facilityName: state.facility?.name ?? null,
      unitLine: null,
      showOrigin: true,
      originFacilityName: state.originFacility?.name ?? null,
      originUnitLine: state.originUnit ? formatStorageUnitLine(state.originUnit) : null
    };
    return hasVisibleLocation(display) ? display : null;
  }

  if (state.placement.value === StoragePlacement.PROCESSING) {
    const display: StorageGroupLocationDisplay = {
      facilityName: state.facility?.name ?? null,
      unitLine: null,
      showOrigin: false,
      originFacilityName: null,
      originUnitLine: null
    };
    return hasVisibleLocation(display) ? display : null;
  }

  if (state.placement.value === StoragePlacement.AT_UNIT && state.storageUnit != null) {
    return {
      facilityName: state.facility?.name ?? state.storageUnit.storageFacility.name,
      unitLine: formatStorageUnitLine(state.storageUnit),
      showOrigin: false,
      originFacilityName: null,
      originUnitLine: null
    };
  }

  if (state.facility) {
    return {
      facilityName: state.facility.name,
      unitLine: null,
      showOrigin: false,
      originFacilityName: null,
      originUnitLine: null
    };
  }

  return null;
};

export const getCurrentStorageFacilityId = (state: StorageObjectGroupStateRecord | null | undefined): number | null =>
  state?.facility?.id ?? state?.storageUnit?.storageFacility?.id ?? null;

// One line for running text: "Rosten · Hall A • 3-2", or the status when it has no place ("With the customer").
export const getStorageGroupLocationLabel = (state: StorageObjectGroupStateRecord | null | undefined): string => {
  const location = getStorageGroupLocationDisplay(state);
  const place = location ? [location.facilityName, location.unitLine].filter(Boolean).join(' · ') : '';
  return place || getStorageGroupStatus(state).label;
};
